/* =========================================================================
   assets/product.js
   Seleção de variante, disponibilidade e lightbox da galeria.
   Substitui o estado React da página de look.
   ========================================================================= */

/* --- <variant-picker> ----------------------------------------------------
   Lê o JSON de variantes renderizado pelo Liquid, atualiza preço,
   input hidden e estado do botão. Sem seleção inicial quando há tamanhos —
   mesma regra do projeto original (escolha consciente).
------------------------------------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.input = document.querySelector('[data-variant-input]');
    this.price = document.querySelector('[data-variant-price]');
    this.button = document.querySelector('[data-add-button]');
    this.chooseLabel = this.dataset.chooseLabel || '';
    this.soldLabel = this.dataset.soldLabel || '';
    this.addLabel = this.dataset.addLabel || '';

    this.addEventListener('click', (event) => {
      const option = event.target.closest('[data-variant-id]');
      if (!option) return;
      this.select(option.dataset.variantId);
    });

    if (this.dataset.autoselect === 'true' && this.variants.length) {
      const first = this.variants.find((v) => v.available) || this.variants[0];
      this.select(String(first.id));
    } else {
      this.setButton(null);
    }
  }

  select(id) {
    const variant = this.variants.find((v) => String(v.id) === String(id));
    this.querySelectorAll('[data-variant-id]').forEach((el) => {
      el.setAttribute('aria-pressed', String(el.dataset.variantId === String(id)));
    });
    if (this.input) this.input.value = id;
    if (this.price && variant) this.price.textContent = variant.price_formatted;
    this.setButton(variant);
  }

  setButton(variant) {
    if (!this.button) return;
    if (!variant) {
      this.button.disabled = true;
      this.button.textContent = this.chooseLabel;
    } else if (!variant.available) {
      this.button.disabled = true;
      this.button.textContent = this.soldLabel;
    } else {
      this.button.disabled = false;
      this.button.textContent = this.addLabel;
    }
  }
}
customElements.define('variant-picker', VariantPicker);

/* --- <fea-lightbox> ------------------------------------------------------
   Galeria em tela cheia com Esc, setas do teclado e navegação por botões.
------------------------------------------------------------------------- */
class FeaLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.image = this.querySelector('[data-lightbox-image]');
    this.sources = JSON.parse(this.querySelector('[data-lightbox-sources]').textContent);
    this.index = 0;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-lightbox-open]');
      if (!trigger) return;
      this.open(Number(trigger.dataset.lightboxOpen));
    });

    this.querySelectorAll('[data-lightbox-step]').forEach((button) => {
      button.addEventListener('click', () => this.step(Number(button.dataset.lightboxStep)));
    });
    this.querySelector('[data-lightbox-close]').addEventListener('click', () => this.dialog.close());

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.step(1);
      if (event.key === 'ArrowLeft') this.step(-1);
    });
    this.dialog.addEventListener('close', () => {
      document.body.classList.remove('drawer-open');
    });
  }

  open(index) {
    this.index = index;
    this.render();
    document.body.classList.add('drawer-open');
    this.dialog.showModal();
  }

  step(delta) {
    this.index = (this.index + delta + this.sources.length) % this.sources.length;
    this.render();
  }

  render() {
    const source = this.sources[this.index];
    if (source) {
      this.image.src = source.src;
      this.image.alt = source.alt || '';
    }
  }
}
customElements.define('fea-lightbox', FeaLightbox);

/* --- <sticky-buy> --------------------------------------------------------
   Barra de compra fixa no celular: aparece quando o botão principal sai
   da tela e espelha o estado dele (texto e bloqueio). Sem tamanho
   escolhido, o toque leva até os tamanhos em vez de comprar.
------------------------------------------------------------------------- */
class StickyBuy extends HTMLElement {
  connectedCallback() {
    this.main = document.querySelector('[data-add-button]');
    this.button = this.querySelector('[data-sticky-button]');
    if (!this.main || !this.button || !('IntersectionObserver' in window)) return;

    const mirror = () => {
      this.button.textContent = this.main.textContent.trim();
      this.button.disabled = this.main.disabled && !this.needsSize();
    };
    mirror();
    new MutationObserver(mirror).observe(this.main, { childList: true, characterData: true, subtree: true, attributes: true });

    new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting;
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.button.tabIndex = visible ? 0 : -1;
    }).observe(this.main);

    this.button.addEventListener('click', () => {
      if (this.needsSize()) {
        const picker = document.querySelector('variant-picker');
        if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.main.click();
    });
  }

  needsSize() {
    const input = document.querySelector('[data-variant-input]');
    return !!document.querySelector('variant-picker') && input && !input.value;
  }
}
customElements.define('sticky-buy', StickyBuy);
